import React, { useState } from 'react';
import { X, CheckCircle, AlertTriangle, Info, XCircle, Filter } from 'lucide-react';
import { EventLogEntry } from '../types';

interface EventLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  entries: EventLogEntry[];
}

const TYPE_CONFIG = {
  success: { icon: CheckCircle, cls: 'text-emerald-400', bg: 'bg-emerald-900/20 border-emerald-800/50', label: 'Success' },
  warning: { icon: AlertTriangle, cls: 'text-amber-400', bg: 'bg-amber-900/20 border-amber-800/50', label: 'Warning' },
  error:   { icon: XCircle,      cls: 'text-red-400',    bg: 'bg-red-900/20 border-red-800/50',       label: 'Error' },
  info:    { icon: Info,         cls: 'text-blue-400',   bg: 'bg-blue-900/10 border-gray-700/50',      label: 'Info' },
} as const;

function formatGameTime(ticks: number): string {
  const TICKS_PER_DAY = 1440;
  const day = Math.floor(ticks / TICKS_PER_DAY) + 1;
  const tickOfDay = ticks % TICKS_PER_DAY;
  const hour = Math.floor((tickOfDay / TICKS_PER_DAY) * 24);
  const min = Math.floor(((tickOfDay / TICKS_PER_DAY) * 24 * 60) % 60);
  return `Day ${day} ${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

type FilterType = 'all' | 'success' | 'warning' | 'error' | 'info';

const EventLogModal: React.FC<EventLogModalProps> = ({ isOpen, onClose, entries }) => {
  const [filter, setFilter] = useState<FilterType>('all');

  if (!isOpen) return null;

  const filtered = filter === 'all' ? entries : entries.filter(e => e.type === filter);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl border border-gray-700 shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 shrink-0">
          <h2 className="text-lg font-bold">Event Log</h2>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">{entries.length} events total</span>
            <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-1 px-5 py-2 border-b border-gray-800 shrink-0">
          <Filter size={13} className="text-gray-500 mr-1" />
          {(['all', 'warning', 'error', 'success', 'info'] as FilterType[]).map(f => {
            const count = f === 'all' ? entries.length : entries.filter(e => e.type === f).length;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                  filter === f ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                }`}
              >
                {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)} ({count})
              </button>
            );
          })}
        </div>

        {/* Entries */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1.5">
          {filtered.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-12">No events yet.</p>
          ) : (
            filtered.map(entry => {
              const cfg = TYPE_CONFIG[entry.type];
              const Icon = cfg.icon;
              return (
                <div
                  key={entry.id}
                  className={`flex items-start gap-3 px-3 py-2 rounded-lg border text-sm ${cfg.bg}`}
                >
                  <Icon size={14} className={`${cfg.cls} mt-0.5 shrink-0`} />
                  <span className="flex-1 text-gray-200 leading-snug">{entry.message}</span>
                  <span className="text-xs text-gray-500 shrink-0 font-mono">{formatGameTime(entry.gameTime)}</span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default EventLogModal;
